// ChatDrawer.jsx

import React, {Suspense, useEffect, useState} from 'react';
import {Box, Drawer, CircularProgress} from '@mui/material';
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import FocusLock from 'react-focus-lock';
import {EntryPoint} from "../Chatbot/EntryPoint";
import Chatbot from "../Chatbot/Chatbot";
import styles from "./ChatDrawer.module.css";
import Constants from "../../utils/Constants";

import FocusOnTop from "../../utils/FocusOnTop";

/**
 * SearchResultsDrawer component, responsible for showing the chat messages and search results
 * @param merchant {object} The merchant object
 * @param isOpen {boolean} Whether the drawer is open
 * @param initialUserMessage {string} The initial message from the user
 * @param initialAssistantMessage {string} The initial message from the assistant
 * @param onClose {function} Callback for closing the drawer
 * @param brandTheme {object} A dictionary of brand theme attributes
 * @param isFirstMessageFromSearchBar {boolean} Whether the query was made from the search bar, if true, we instruct the API to search immediately instead of running an agent loop for the first request
 * @param entryPoint {string} The entry point for the chatbot
 * @param assistantConfiguration {object} The assistant configuration
 * @param initialCategories {Array} The initial categories of product variants to show
 * @param cartProductVariantInternalId {string} The product variant ID in the cart
 * @returns {Element} The SearchResultsDrawer component
 */
const ChatDrawer = ({
    merchant,
    isOpen,
    initialUserMessage,
    initialAssistantMessage,
    onClose,
    brandTheme,
    entryPoint,
    assistantConfiguration,
    initialCategories,
    cartProductVariantInternalId }) => {

    const [cartWidth, setCartWidth] = useState(null);

    useEffect(() => {
        if (entryPoint === EntryPoint.RespondingFromCartAssistant) {
            const w = getCartWidth();
            setCartWidth(w);
        }
    }, [entryPoint, assistantConfiguration]);

    function closeDrawer() {
        if (window.innerWidth < Constants.MaxWidthForMobile) {
            FocusOnTop();
        }
        onClose();
    }

    function getScrollbarWidth() {
        return (window.innerWidth || 0) - (document.documentElement?.clientWidth || 0);
    }

    function getCartWidth() {
        let cartElementSelector = assistantConfiguration?.cartAssistant?.cartSelector;
        if (!cartElementSelector) {
            cartElementSelector = assistantConfiguration?.cartAssistant?.containerSelector;
            if (!cartElementSelector) {
                return null;
            }
        }

        // Check if the cart element is in the DOM
        let cartElement = document.querySelector(cartElementSelector);
        if (!cartElement) {
            return null;
        }

        // Check if the cart element is visible
        if (cartElement.offsetParent === null) {
            return null;
        }

        // Get the width of the cart element
        let boundingClientRect = cartElement.getBoundingClientRect();
        let cartWidth = boundingClientRect.width;
        if (cartWidth > 0) {
            return cartWidth;
        }
        let cartStyle = window.getComputedStyle(cartElement);
        let cartWidthFromStyle = cartStyle.width;
        if (cartWidthFromStyle) {
            return parseInt(cartWidthFromStyle.replace("px", ""));
        }
        return boundingClientRect.right - boundingClientRect.left;
    }

    function getPaperStyles() {
        let drawerPaperStyles = {
            width: "min(1200px, 100vw)",
            maxWidth: "min(1200px, 100vw)",
            margin: "0 auto",
        };

        if (entryPoint === EntryPoint.RespondingFromCartAssistant) {
            let cartWidth = getCartWidth();
            let scrollbarWidth = getScrollbarWidth() || 0;

            if (cartWidth) {
                drawerPaperStyles.right = `${cartWidth + scrollbarWidth}px`;
            }
            drawerPaperStyles.boxShadow = 'none';
        }

        return drawerPaperStyles;
    }

    function renderChatbot() {
        return merchant && (
            <Suspense fallback={<CircularProgress size={16}/>}>
                <Chatbot
                    merchant={merchant}
                    initialUserMessage={initialUserMessage}
                    initialAssistantMessage={initialAssistantMessage}
                    initialCategories={initialCategories}
                    brandTheme={brandTheme}
                    onClose={closeDrawer}
                    entryPoint={entryPoint}
                    assistantConfiguration={assistantConfiguration}
                    cartProductVariantInternalId={cartProductVariantInternalId}
                />
            </Suspense>
        )
    }

    function renderCloseIcon() {
        return <IconButton
            aria-label="Close"
            data-testid={"close-icon"}
            id={styles.drawerCloseIcon}
            onClick={closeDrawer}>
            <CloseIcon
                sx={{
                    fontSize: 30,
                    stroke: "white",
                    fill: "white",
                    strokeWidth: 1
                }}
            />
        </IconButton>;
    }

    function renderChatbotAndCloseIcon() {
        return <Box id={styles.drawerContents}>
            {renderChatbot()}
            {renderCloseIcon()}
        </Box>;
    }

    function renderDrawer() {
        let scrollbarWidth = getScrollbarWidth();

        return <Drawer
            anchor="bottom"
            id={styles.chatDrawer}
            open={isOpen}
            onClose={closeDrawer}
            data-testid="chat-drawer"
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: "#00000066",
                        width: entryPoint === EntryPoint.RespondingFromCartAssistant && window.innerWidth >= Constants.MaxWidthForMobile ? `calc(100% - ${cartWidth + scrollbarWidth}px)` : '100%',
                    }
                },
                paper: {
                    className: styles.drawerPaper,
                    elevation: 0,
                    sx: getPaperStyles()
                }
            }}>

            {renderChatbotAndCloseIcon()}
        </Drawer>
    }

    return (
        <FocusLock autoFocus={false}>
            {renderDrawer()}
        </FocusLock>
    );
};

export default ChatDrawer;
